import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { apiService } from '../services/api';
import { getAddonsCount } from '../utils/addons';
import { isCafeOpenNow } from '../utils/workingHours';
import { isCafeInactive } from '../utils/coffeeShop';
import ItemCustomizationModal from './ItemCustomizationModal';
import NotificationBell from './NotificationBell';

const MenuView = () => {
  const { cafeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editOrderId = searchParams.get('editOrderId');
  const { 
    addItem, 
    getTotalItems, 
    selectedCafe, 
    getCafeById, 
    setActiveCafe 
  } = useCart();
  const [activeTab, setActiveTab] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [customizingItem, setCustomizingItem] = useState(null);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [cafeDetails, setCafeDetails] = useState(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const lastScrollY = React.useRef(0);

  // Загрузка меню и данных кофейни (для рабочих часов) при монтировании
  useEffect(() => {
    const loadMenuAndCafe = async () => {
      try {
        setLoading(true);
        setError(null);
        const [items, cafe] = await Promise.all([
          apiService.getMenuItems(cafeId),
          apiService.getCoffeeShop(cafeId).catch(() => null)
        ]);
        setMenuItems(items);
        setCafeDetails(cafe || null);

        // Создаем категории из уникальных типов
        const categoriesFromAPI = apiService.getCategoriesFromMenuItems(items);
        setCategories(categoriesFromAPI);

        // Устанавливаем первую категорию как активную
        if (categoriesFromAPI.length > 0) {
          setActiveTab(categoriesFromAPI[0].id);
        }
      } catch (err) {
        setError(err.message);
        console.error('Ошибка загрузки меню:', err);
      } finally {
        setLoading(false);
      }
    };

    if (cafeId) {
      loadMenuAndCafe();
    }
  }, [cafeId]);

  const numericCafeId = Number(cafeId);

  useEffect(() => {
    if (numericCafeId) {
      setActiveCafe(numericCafeId);
    }
  }, [numericCafeId, setActiveCafe]);

  // Пересчёт «открыто/закрыто» после полуночи и при возврате в приложение
  useEffect(() => {
    const refresh = () => setNowTick(Date.now());
    const id = setInterval(refresh, 30000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Скрываем шапку при скроле вниз
  useEffect(() => {
    const container = document.querySelector('.menu-container');
    if (!container) return;
    const handleScroll = () => {
      const currentScrollY = container.scrollTop;
      if (currentScrollY > lastScrollY.current && currentScrollY > 30) {
        setHeaderHidden(true);
      } else {
        setHeaderHidden(false);
      }
      lastScrollY.current = currentScrollY;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const resolveCafeInfo = () => {
    if (selectedCafe && Number(selectedCafe.id) === numericCafeId) {
      return selectedCafe;
    }
    return getCafeById(numericCafeId) || selectedCafe || {
      id: numericCafeId,
      name: `Кофейня #${cafeId}`
    };
  };

  const openStatus = cafeDetails
    ? isCafeOpenNow(cafeDetails, new Date(nowTick))
    : { isOpen: true };
  const isCafeClosed = !openStatus.isOpen;
  const isShopInactive =
    cafeDetails != null ? isCafeInactive(cafeDetails) : isCafeInactive(resolveCafeInfo());
  const cannotOrder = isCafeClosed || isShopInactive;

  const handleAddToCart = (item) => {
    if (cannotOrder) {
      return;
    }
    // Если у товара есть размеры или допники, показываем модальное окно
    const hasSizes = Array.isArray(item.sizes) && item.sizes.length > 0;
    const hasAddons = getAddonsCount(item.addons) > 0;

    if (hasSizes || hasAddons) {
      setCustomizingItem(item);
      setIsCustomizationOpen(true);
    } else {
      // Если нет кастомизации, добавляем сразу
      const cafeInfo = resolveCafeInfo();
      addItem(cafeInfo, {
        ...item,
        quantity: 1,
        size: null,
        size_id: null,
        customizations: {}
      });
    }
  };

  const handleCustomizationConfirm = (preparedItem) => {
    if (cannotOrder) return;
    const cafeInfo = resolveCafeInfo();
    addItem(cafeInfo, preparedItem);
    setIsCustomizationOpen(false);
    setCustomizingItem(null);
  };

  const handleCustomizationClose = () => {
    setIsCustomizationOpen(false);
    setCustomizingItem(null);
  };

  const handleCartClick = () => {
    // Сохраняем editOrderId в URL при переходе в корзину
    const url = editOrderId ? `/cart?editOrderId=${editOrderId}` : '/cart';
    navigate(url);
  };

  const handleBackToMap = () => {
    navigate('/');
  };

  const handleImageError = (itemId) => {
    setImageErrors(prev => ({ ...prev, [itemId]: true }));
  };

  // Функция для получения минимальной цены товара
  const getItemPrice = (item) => {
    // Если у товара есть размеры, находим минимальную цену среди всех размеров
    if (Array.isArray(item.sizes) && item.sizes.length > 0) {
      const prices = item.sizes.map(size => Number(size.price || 0)).filter(p => p > 0);
      if (prices.length > 0) {
        const minPrice = Math.min(...prices);
        return { price: minPrice, hasSizes: true };
      }
    }
    // Если размеров нет или все цены равны 0, используем базовую цену
    return { price: Number(item.price || 0), hasSizes: false };
  };

  // Получаем элементы текущей категории
  const currentItems = menuItems.filter(item => item.type === activeTab);

  const cafeInfo = resolveCafeInfo();
  const cafeName = cafeInfo?.name || 'Кофейня';
  const itemsInCartCount = getTotalItems(cafeInfo?.id);
  const itemsInCartLabel = itemsInCartCount
    ? `${itemsInCartCount} ${itemsInCartCount === 1 ? 'товар' : itemsInCartCount >= 5 ? 'товаров' : 'товара'}`
    : 'Корзина пуста';

  // Если меню пустое
  if (!loading && !error && menuItems.length === 0) {
    return (
      <div className="menu-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>RCoffee</h1>
              <p className="subtitle">{cafeName} • {itemsInCartLabel}</p>
            </div>
            {!editOrderId && (
            <button 
              className="btn"
              onClick={handleBackToMap}
              style={{ 
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Назад
            </button>
            )}
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: '#333',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '12px',
          margin: '20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
          <div style={{ marginBottom: '16px', fontWeight: '600' }}>Меню пока пустое</div>
          <div style={{ fontSize: '14px', opacity: 0.7, color: '#666' }}>
            В этой кофейне пока нет доступных позиций меню
          </div>
        </div>
      </div>
    );
  }

  // Если идет загрузка
  if (loading) {
    return (
      <div className="menu-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>RCoffee</h1>
              <p className="subtitle">{cafeName} • Загрузка меню...</p>
            </div>
            {!editOrderId && (
            <button 
              className="btn"
              onClick={handleBackToMap}
              style={{ 
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Назад
            </button>
            )}
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: 'white',
          fontSize: '18px'
        }}>
          <div>Загружаем меню...</div>
        </div>
      </div>
    );
  }

  // Если произошла ошибка
  if (error) {
    return (
      <div className="menu-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>RCoffee</h1>
              <p className="subtitle">{cafeName} • Ошибка загрузки</p>
            </div>
            {!editOrderId && (
            <button 
              className="btn"
              onClick={handleBackToMap}
              style={{ 
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Назад
            </button>
            )}
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
          <div style={{ marginBottom: '16px' }}>{error}</div>
          <button 
            className="btn"
            onClick={() => window.location.reload()}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const handleOrdersClick = () => {
    navigate('/orders');
  };

  return (
    <div className="menu-container">
      <div className={`header ${headerHidden ? 'header-hidden' : ''}`}>
        <div className="flex space-between align-center" style={{ marginBottom: '8px' }}>
          <div>
            {!editOrderId && (
            <button 
              className="btn"
              onClick={handleBackToMap}
              style={{ 
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Назад
            </button>
            )}
            {editOrderId && (
              <p style={{
                fontSize: '14px',
                color: '#ff9800',
                fontWeight: '500',
                background: 'rgba(255, 152, 0, 0.1)',
                padding: '6px 12px',
                borderRadius: '8px',
                display: 'inline-block',
                margin: 0
              }}>
                Режим редактирования заказа #{editOrderId}
              </p>
            )}
          </div>
          <h1 style={{ margin: 0 }}>{cafeName}</h1>
          <div className="header-right-actions">
            <NotificationBell />
            <button 
              className="btn"
              onClick={handleOrdersClick}
              style={{ 
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Заказы
            </button>
          </div>
        </div>
      </div>

      {isShopInactive && (
        <div className="menu-soon-banner" role="status">
          <strong className="menu-soon-banner-title">Скоро</strong>
          <p className="menu-soon-banner-text">
            Кофейня пока не принимает заказы. Меню ниже можно только просматривать.
          </p>
        </div>
      )}

      {isCafeClosed && (
        <div
          className="menu-closed-banner"
          style={{
            background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
            color: '#fff',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          Сейчас кофейня закрыта. Заказ недоступен.
          {openStatus.todayHours && (
            <div style={{ marginTop: '4px', opacity: 0.9, fontSize: '13px' }}>
              {openStatus.todayHours}
            </div>
          )}
        </div>
      )}

      <div className="category-tabs">
        {categories.map(category => (
          <span
            key={category.id}
            className={`category-tab ${activeTab === category.id ? 'active' : ''}`}
            onClick={() => setActiveTab(category.id)}
          >
            {category.name}
          </span>
        ))}
      </div>

      <div className="menu-section">
        <div className="menu-items">
          {currentItems.length > 0 ? (
            currentItems.map(item => (
              <div key={item.id} className="menu-item">
                <div className="menu-item-image">
                  {imageErrors[item.id] ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      color: '#999',
                      fontSize: '14px',
                      textAlign: 'center',
                      padding: '10px'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '8px' }}></div>
                      <div>Фото недоступно</div>
                    </div>
                  ) : (
                    <img 
                      src={item.image} 
                      alt={item.name}
                      onError={() => handleImageError(item.id)}
                      // style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                    />
                  )}
                </div>
                <h3>{item.name}</h3>
                <p>{item.description}</p>
                <div className="menu-item-price">
                  {(() => {
                    const { price, hasSizes } = getItemPrice(item);
                    return hasSizes ? `от ${price} ₽` : `${price} ₽`;
                  })()}
                </div>
                <button 
                  className="btn"
                  onClick={() => handleAddToCart(item)}
                  disabled={cannotOrder}
                  title={
                    isShopInactive
                      ? 'Кофейня временно не принимает заказы'
                      : isCafeClosed
                        ? (openStatus.reason || 'Сейчас кофейня закрыта')
                        : undefined
                  }
                  style={cannotOrder ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                >
                  Добавить в корзину
                </button>
              </div>
            ))
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#333',
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              margin: '20px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
              <div style={{ fontSize: '18px', marginBottom: '8px', fontWeight: '600' }}>
                В этой категории пока нет товаров
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7, color: '#666' }}>
                Попробуйте выбрать другую категорию
              </div>
            </div>
          )}
        </div>
      </div>

      {getTotalItems() > 0 && (
        <button 
          className="floating-cart-btn"
          onClick={handleCartClick}
        >
          <span className="cart-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-cart4" viewBox="0 0 16 16"> <path d="M0 2.5A.5.5 0 0 1 .5 2H2a.5.5 0 0 1 .485.379L2.89 4H14.5a.5.5 0 0 1 .485.621l-1.5 6A.5.5 0 0 1 13 11H4a.5.5 0 0 1-.485-.379L1.61 3H.5a.5.5 0 0 1-.5-.5zM3.14 5l.5 2H5V5H3.14zM6 5v2h2V5H6zm3 0v2h2V5H9zm3 0v2h1.36l.5-2H12zm1.11 3H12v2h.61l.5-2zM11 8H9v2h2V8zM8 8H6v2h2V8zM5 8H3.89l.5 2H5V8zm0 5a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm9-1a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm-2 1a2 2 0 1 1 4 0 2 2 0 0 1-4 0z"/> </svg>
          </span>
          Перейти в корзину ({getTotalItems()} товаров)
        </button>
      )}

      <ItemCustomizationModal
        item={customizingItem}
        isOpen={isCustomizationOpen}
        onClose={handleCustomizationClose}
        onConfirm={handleCustomizationConfirm}
      />
    </div>
  );
};

export default MenuView;
